import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GetPendingEmailDomainResponseInterface } from '@vendasta/email';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { DomainCacheService } from '../domain-cache.service';
import { SenderID } from '../shared-types';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { EmailUII18nModule } from '../../../src/lib/i18n/email-ui-i18n.module';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

export interface NewDomainDialogData {
  senderID: SenderID;
  enableDomainTypeSelection?: boolean;
}

export interface Result {
  newPendingDomain?: string;
}

@Component({
  selector: 'email-new-domain-dialog',
  templateUrl: './new-domain-dialog.component.html',
  styleUrls: ['./new-domain-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    TranslateModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyPipesModule,
    EmailUII18nModule,
    TranslateModule,
    MatDialogModule,
    GalaxyFormFieldModule,
  ],
})
export class NewDomainDialogComponent {
  readonly senderForm = new UntypedFormGroup({
    senderDomain: new UntypedFormControl('', [Validators.required, Validators.pattern('[^(@\\s)]+\\.[^(@\\s)]+')]),
    shareWithOtherBusiness: new UntypedFormControl(false),
  });

  get enableDomainTypeSelection(): boolean {
    return this.data.enableDomainTypeSelection || false;
  }
  authorizedDomain$: Observable<GetPendingEmailDomainResponseInterface> = of(null);

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: NewDomainDialogData,
    @Inject(MatDialogRef) private readonly dialog: MatDialogRef<NewDomainDialogComponent>,
    private readonly domainService: DomainCacheService,
    private readonly alerts: SnackbarService,
  ) {}

  private authorize(): void {
    const domain = this.senderForm.get('senderDomain').value;

    let options = undefined;

    // Only handle domain type if the feature is enabled for this app
    if (this.enableDomainTypeSelection) {
      const isShared = this.senderForm.get('shareWithOtherBusiness').value;
      // Only pass domain type if it's shared (checked), otherwise let backend use default
      options = isShared ? { domainType: 1 } : undefined; // DomainType_DOMAIN_TYPE_SHARED = 1
    }

    this.authorizedDomain$ = this.domainService
      .createPendingDomain(this.data.senderID.senderType, this.data.senderID.senderID, domain, options)
      .pipe(
        shareReplay({ refCount: true, bufferSize: 1 }),
        catchError(() => {
          this.alerts.openErrorSnack('DOMAIN_AUTHORIZATION.NEW_DOMAIN_DIALOG.AUTH_ERROR');
          return of(null);
        }),
        tap(() =>
          this.dialog.close({
            newPendingDomain: domain,
          } as Result),
        ),
      );
  }

  close(): void {
    this.dialog.close({ newPendingDomain: undefined } as Result);
  }

  submitForm(): void {
    this.senderForm.markAsDirty();
    if (this.senderForm.valid) {
      this.authorize();
    } else {
      this.alerts.openErrorSnack('DOMAIN_AUTHORIZATION.NEW_DOMAIN_DIALOG.INVALID_ERROR');
    }
  }
}
